import { useEffect } from 'react'

/**
 * **Den Grund des Fensters wirklich durchsichtig machen.**
 *
 * Gemeldet am 18.09.2026 mit Bild: unter dem Fokus-Fenster ein heller Streifen. Das
 * Fenster ist `transparent: true` und das Fokus-Fenster misst seine Höhe selbst — bei
 * einzeiligem Titel rund 35 px in einem 58 px hohen Fenster. Was darunter
 * durchscheint, malt die Seite.
 *
 * **Und zwar der `body`, mit `!important` — gleich zweimal:**
 *
 * 1. `styles/app.scss`: `body { background-color: var(--background) !important }`
 * 2. Der zur Laufzeit eingesetzte `#theme-override` aus `index.html`:
 *    `:root, .dark, body, html { background-color: … !important }` — er wird
 *    bei **jedem** `focus` des Fensters neu geschrieben.
 *
 * Beide sind für jedes andere Fenster richtig: Sie verhindern das Weißblitzen
 * beim Start. Im Fokus-Fenster und in der Schnelleingabe (#234) sind sie falsch —
 * beide sind durchsichtige Fenster, deren Karte Fläche und Schatten selbst malt.
 *
 * **Darum `setProperty(…, 'important')` und nicht die schlichte Zuweisung.** Ein
 * gewöhnlicher Stil am Element verliert gegen eine `!important`-Regel aus einer
 * Datei — nur ein `!important` AM Element steht darüber. Der erste Versuch ohne
 * das Wort machte deshalb nur `#app` durchsichtig (dort gibt es keine solche
 * Regel) und den `body` nicht: sichtbar als Streifen, der links und rechts
 * verschwand, unten aber blieb.
 *
 * Gemessen statt geraten — der Weg dahin steht in `.claude/tasks/kernschleife.md`.
 *
 * Wird beim Verlassen zurückgenommen: Das Fokus-Fenster ist eine **Route** im
 * Haupt-Bundle (ADR 0025), dasselbe Dokument könnte danach etwas anderes zeigen.
 */
export function useTransparentBackdrop(): void {
  useEffect(() => {
    const ziele = [document.documentElement, document.body, document.getElementById('app')]
      .filter((el): el is HTMLElement => el != null)
    const vorher = ziele.map(el => el.style.getPropertyValue('background-color'))

    for (const el of ziele) el.style.setProperty('background-color', 'transparent', 'important')

    return () => {
      ziele.forEach((el, i) => {
        el.style.removeProperty('background-color')
        if (vorher[i]) el.style.setProperty('background-color', vorher[i]!)
      })
    }
  }, [])
}
